import axios, { AxiosInstance } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { log, LogLevel } from "logging/datadogBrowserLogs";

/**
 * Register an interceptor on an axios client that will handle 401 responses and attempt to
 * force a refresh of the session before retrying the request.
 *
 * Reason:
 * If the access token has become invalid or corrupted for some reason other than being expired (e.g. tampered with in local storage),
 * Amplify Auth will not perform an auto-refresh of the session.
 * This should not normally happen.
 *
 */
export const registerAxiosAuthInterceptor = (axiosClient: AxiosInstance) => {
  axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          // Force a refresh of the session to get a new access token
          const session = await fetchAuthSession({ forceRefresh: true });
          const token = session?.tokens?.accessToken;

          if (!token) {
            return Promise.reject(error);
          }

          // Update the original request with the new access token before retrying
          originalRequest.headers.Authorization = `Bearer ${token.toString()}`;
          return axios(originalRequest);
        } catch (err) {
          log(
            LogLevel.error,
            "axiosClient.interceptors.response",
            `Call to ${originalRequest.url} failed with status 401. Failed to refresh access token and retry request`,
            err,
          );

          return Promise.reject(err);
        }
      }

      if (originalRequest && error.response) {
        if (error.response.status === 404)
          return Promise.resolve(error.response);

        log(
          LogLevel.error,
          "axiosClient.interceptors.response",
          `Call to ${originalRequest.url} failed with status ${error.response.status} - ${error.response.statusText}.`,
          error,
        );

        return Promise.reject(error);
      }

      log(
        LogLevel.error,
        "axiosClient.interceptors.response",
        `Request failed - ${error.message}`,
        error,
      );

      return Promise.reject(error);
    },
  );
};
